import { toast } from 'sonner';
import { browserApiOrigin } from '@projoint/shared';

import apiMessages from '@/shared/i18n/en/lib-api';
import { tr } from '@/shared/i18n/translate';
import { captureAuthLifetime, getAccessToken, getAuthIdentityGeneration, isAuthLifetimeCurrent, useAuthStore, type AuthLifetime } from '@/shared/stores/auth.store';

export const API_BASE = browserApiOrigin(import.meta.env.VITE_API_URL, window.location.origin);

/**
 * Throttle the "session expired" toast — multiple parallel queries all
 * hitting 401 at the same time would otherwise stack a tower of identical
 * banners on screen.
 */
let lastSessionExpiredToastAt = 0;
function notifySessionExpired(): void {
  const now = Date.now();
  if (now - lastSessionExpiredToastAt < 5_000) return;
  lastSessionExpiredToastAt = now;
  toast.error(tr(apiMessages, 'sessionExpired'), {
    description: tr(apiMessages, 'sessionExpiredBody'),
  });
}

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: unknown;
    correlationId?: string;
  };
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiErrorBody,
    /** Seconds to wait before retrying (429/503) — from `Retry-After` or the
     *  body's `error.retryAfterSec`; null when the server didn't say. */
    public readonly retryAfterSec: number | null = null,
  ) {
    super(body.error.message);
    this.name = 'ApiError';
  }

  get code(): string {
    return this.body.error.code;
  }
}

interface ApiOptions {
  /** Bound an interactive read; omitted for long-running provider operations. */
  timeoutMs?: number;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** JSON body — already a plain object. */
  body?: unknown;
  /** Idempotency-Key to send. The caller decides whether the operation deserves one. */
  idempotencyKey?: string;
  /** Skip the auth header (for /auth/signup, /auth/signin, password resets, etc.) */
  unauthenticated?: boolean;
  /** Skip the refresh-on-401 retry — used by the /auth/refresh endpoint itself. */
  skipRefresh?: boolean;
  /** Renew an expired JWT BEFORE a proof-protected mutation, while the refresh
   * credential is still live. A failed password/factor proof is never replayed. */
  refreshExpiredAccess?: boolean;
  /** Preserve a successful credential-change response (including one-time codes)
   * if revocation already cleared this tab. Never permit it over a new sign-in. */
  allowAfterSessionEnds?: boolean;
  /** Extra headers — overrides defaults. */
  headers?: Record<string, string>;
}

/**
 * Fetch wrapper with three things you always want and never want to forget:
 *   1. Auth header from the auth store.
 *   2. Refresh-on-401 retry — calls /auth/refresh, then re-tries the original request once.
 *   3. Idempotency-Key passthrough.
 *
 * Returns parsed JSON. On error, throws ApiError with the structured envelope.
 */
export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  if (opts.timeoutMs === undefined) return request<T>(path, opts);
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout>;
  const deadline = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => {
      reject(new ApiError(0, { error: { code: 'request_timeout', message: tr(apiMessages, 'requestTimeout') } }));
      controller.abort();
    }, opts.timeoutMs);
  });
  try {
    // Covers headers AND response parsing, including a stalled JSON body.
    return await Promise.race([request<T>(path, opts, controller.signal), deadline]);
  } finally {
    clearTimeout(timer!);
  }
}

async function request<T>(path: string, opts: ApiOptions, signal?: AbortSignal): Promise<T> {
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
  const sentToken = opts.unauthenticated ? null : getAccessToken();
  const lifetime = captureAuthLifetime();
  if (opts.refreshExpiredAccess && accessTokenExpired(sentToken)) {
    const outcome = await refreshSession();
    assertRequestSession(lifetime, opts);
    if (outcome !== 'refreshed') throw new ApiError(outcome === 'unavailable' ? 503 : 401, {
      error: { code: 'session_renewal_required', message: outcome === 'unavailable' ? tr(apiMessages, 'renewFailed') : tr(apiMessages, 'sessionEnded') },
    });
  }
  const init = buildRequestInit(opts);
  if (signal) init.signal = signal;

  let response = await fetchOrThrowFriendly(url, init);
  assertRequestSession(lifetime, opts, response.ok);

  // 401 → try one silent refresh (against the httpOnly cookie) + retry once.
  if (response.status === 401 && sentToken && !opts.unauthenticated && !opts.skipRefresh) {
    // A parallel refresh (this tab or another, via the broadcast) may already
    // have replaced the token this request went out with — just retry.
    const current = getAccessToken();
    const outcome = current && current !== sentToken ? 'refreshed' : await refreshSession();
    assertRequestSession(lifetime, opts);
    if (outcome === 'refreshed') {
      const retryInit = buildRequestInit(opts);
      if (signal) retryInit.signal = signal;
      response = await fetchOrThrowFriendly(url, retryInit);
      assertRequestSession(lifetime, opts, response.ok);
    } else if (outcome === 'logged_out') {
      // The server said the session is gone (401/403 from refresh) — clear
      // store; route guard in __root.tsx redirects to /login on the next
      // render.  A single throttled toast so the user isn't left wondering.
      const hadSession = !!useAuthStore.getState().accessToken;
      useAuthStore.getState().clear();
      if (hadSession) notifySessionExpired();
    } else {
      // Refresh was rate-limited / the server hiccuped / we're offline.  The
      // session is still valid server-side, so KEEP it — a 429 or a deploy's
      // 502 must never look like a logout.  Fail just this request (5xx →
      // React Query retries it); the next call refreshes again.
      throw new ApiError(503, {
        error: {
          code: 'session_refresh_unavailable',
          message: tr(apiMessages, 'refreshUnavailable'),
        },
      });
    }
  }

  if (!response.ok) {
    // Friendly fallback when the error body isn't our JSON envelope (a 5xx HTML
    // page, a proxy/gateway error, etc.) — so callers that show `err.message`
    // never surface a raw "Internal Server Error" / empty status text.
    const body = await safeJson<ApiErrorBody>(response, {
      error: {
        code: 'unexpected_error',
        message:
          response.status >= 500
            ? tr(apiMessages, 'serverError')
            : tr(apiMessages, 'genericError'),
      },
    });
    assertRequestSession(lifetime, opts);
    const retryAfterSec = retryAfterSecFrom(response, body);
    throw new ApiError(
      response.status,
      response.status === 429 ? withFriendlyRateLimitMessage(body, retryAfterSec) : body,
      retryAfterSec,
    );
  }

  if (response.status === 204) return undefined as T;
  const result = await safeJson<T>(response, undefined as unknown as T);
  assertRequestSession(lifetime, opts, true);
  return result;
}

function assertRequestSession(lifetime: AuthLifetime, opts: ApiOptions, success = false): void {
  const state = useAuthStore.getState();
  if (opts.unauthenticated || isAuthLifetimeCurrent(lifetime)) return;
  if (success && opts.allowAfterSessionEnds && lifetime.identity === getAuthIdentityGeneration() && !state.accessToken && !state.user) return;
  throw new ApiError(401, { error: { code: 'session_changed', message: tr(apiMessages, 'sessionChanged') } });
}

function accessTokenExpired(token: string | null): boolean {
  try {
    const payload = JSON.parse(atob(token!.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: unknown };
    return typeof payload.exp === 'number' && payload.exp * 1000 <= Date.now() + 30_000;
  } catch { return false; }
}

/**
 * `fetch`, but a network-level failure (offline, DNS, connection refused, CORS
 * preflight reject) becomes an ApiError with a human message instead of a raw
 * `TypeError: Failed to fetch`.  This means every caller that surfaces
 * `err.message` shows something readable when the server is unreachable,
 * without each form having to special-case it.
 */
async function fetchOrThrowFriendly(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch {
    throw new ApiError(0, {
      error: {
        code: 'network_error',
        message: tr(apiMessages, 'networkError'),
      },
    });
  }
}

function buildRequestInit(opts: ApiOptions): RequestInit {
  const headers: Record<string, string> = {
    // Declare the app: (1) names the refresh cookie `projoint_rt` (admin uses
    // `projoint_admin_rt`), and (2) doubles as the CSRF token for /auth/refresh
    // — a cross-origin attacker can't set this custom header (CORS preflight).
    'X-Projoint-App': 'web',
    ...opts.headers,
  };

  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  if (!opts.unauthenticated) {
    const token = getAccessToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }

  if (opts.idempotencyKey) {
    headers['Idempotency-Key'] = opts.idempotencyKey;
  }

  const init: RequestInit = {
    method: opts.method ?? 'GET',
    headers,
    credentials: 'include',
  };
  if (opts.body !== undefined) {
    init.body = JSON.stringify(opts.body);
  }
  return init;
}

// ── session refresh ─────────────────────────────────────────────────────────

/**
 * What a refresh attempt concluded.  ONLY `logged_out` (the server answered
 * 401/403) means the session is gone; `unavailable` (429, 5xx, network) means
 * "couldn't renew right now" and the caller must keep the session.
 */
export type RefreshOutcome = 'refreshed' | 'logged_out' | 'unavailable';

interface RefreshOptions {
  /** Network attempts before reporting `unavailable` (default 3). */
  attempts?: number;
}

/** `adopted`: the cookie was another account's and this token-less tab took it over. */
type AttemptResult = { kind: 'refreshed' | 'logged_out' | 'adopted' } | { kind: 'unavailable'; retryAfterMs: number | null };

const REFRESH_LOCK = 'projoint-refresh';
const AUTH_CHANNEL = 'projoint-auth';
const MAX_RETRY_WAIT_MS = 30_000;
const LOCK_WAIT_MS = 10_000;

/** Single in-flight refresh per tab — parallel 401s share the work. */
let pendingRefresh: Promise<RefreshOutcome> | null = null;
let pendingRefreshGeneration: number | null = null;
let pendingRefreshIdentity: number | null = null;

/**
 * Mint a fresh access token from the httpOnly refresh cookie.
 *
 * The refresh token travels in the `projoint_rt` cookie (`credentials:'include'`
 * sends it), never in the body — so JS doesn't touch it.  Used both for the
 * 401-retry above and for the boot bootstrap that re-establishes a session on
 * page reload (`useAuthBootstrap`).
 *
 * Transient failures (429 / 5xx / network) are retried with backoff, honouring
 * Retry-After.  Across tabs, the network call runs under a Web Lock and the new
 * access token is broadcast, so a tab that waited for the lock reuses it
 * instead of rotating the refresh token a second time.
 */
export function refreshSession(opts: RefreshOptions = {}): Promise<RefreshOutcome> {
  const state = useAuthStore.getState();
  if (!state.accessToken && !state.user && state.bootstrapped) return Promise.resolve('logged_out');
  const generation = state.generation;
  const lifetime = captureAuthLifetime();
  if (!pendingRefresh || pendingRefreshGeneration !== generation || pendingRefreshIdentity !== lifetime.identity) {
    const pending = refreshWithRetry(Math.max(1, opts.attempts ?? 3), lifetime).finally(() => {
      if (pendingRefresh === pending) {
        pendingRefresh = null;
        pendingRefreshGeneration = null;
        pendingRefreshIdentity = null;
      }
    });
    pendingRefresh = pending;
    pendingRefreshGeneration = generation;
    pendingRefreshIdentity = lifetime.identity;
  }
  return pendingRefresh;
}

/** Back-compat boolean form: true iff a new access token was set. */
export async function silentRefresh(): Promise<boolean> {
  return (await refreshSession()) === 'refreshed';
}

async function refreshWithRetry(attempts: number, lifetime: AuthLifetime): Promise<RefreshOutcome> {
  const tokenBefore = getAccessToken();
  for (let attempt = 1; ; attempt += 1) {
    if (!isAuthLifetimeCurrent(lifetime)) return 'logged_out';
    const result = await withRefreshLock(() =>
      // Another tab refreshed while we waited for the lock — reuse its token.
      !isAuthLifetimeCurrent(lifetime)
        ? Promise.resolve<AttemptResult>({ kind: 'logged_out' })
        : tokenChangedSince(tokenBefore) ? Promise.resolve<AttemptResult>({ kind: 'refreshed' }) : refreshOnce(lifetime),
    );
    // Adoption replaced the identity on purpose, so the old lifetime is over.
    if (result.kind === 'adopted') return 'refreshed';
    if (!isAuthLifetimeCurrent(lifetime)) return 'logged_out';
    if (result.kind !== 'unavailable') return result.kind;
    if (attempt >= attempts) return 'unavailable';
    await sleep(retryDelayMs(attempt, result.retryAfterMs));
    if (!isAuthLifetimeCurrent(lifetime)) return 'logged_out';
    if (tokenChangedSince(tokenBefore)) return 'refreshed';
  }
}

async function refreshOnce(lifetime: AuthLifetime): Promise<AttemptResult> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/api/auth/refresh`, {
      method: 'POST',
      // `X-Projoint-App` is required by the endpoint's CSRF guard and also
      // selects the per-app refresh cookie (`projoint_rt`).
      headers: { 'Content-Type': 'application/json', 'X-Projoint-App': 'web' },
      body: '{}',
      credentials: 'include',
    });
  } catch {
    return { kind: 'unavailable', retryAfterMs: null };
  }
  if (!isAuthLifetimeCurrent(lifetime)) return { kind: 'logged_out' };
  if (response.status === 401 || response.status === 403) return { kind: 'logged_out' };
  if (!response.ok) {
    const body = await safeJson<Partial<ApiErrorBody> | null>(response, null);
    const sec = retryAfterSecFrom(response, body);
    return { kind: 'unavailable', retryAfterMs: sec === null ? null : sec * 1000 };
  }
  const tokens = await safeJson<{ accessToken?: unknown } | null>(response, null);
  if (!isAuthLifetimeCurrent(lifetime)) return { kind: 'logged_out' };
  if (typeof tokens?.accessToken !== 'string' || !tokens.accessToken) {
    return { kind: 'unavailable', retryAfterMs: null };
  }
  const subject = jwtSubject(tokens.accessToken);
  const state = useAuthStore.getState();
  if (subject && state.user && subject !== state.user.id) {
    // The cookie jar holds ANOTHER account now. A live tab never swaps identity
    // under its open UI; a tab with no live token (reload / PWA relaunch) only
    // has a stale snapshot — the cookie is the truth, so open that account.
    if (state.accessToken) return { kind: 'logged_out' };
    state.adoptSession({ accessToken: tokens.accessToken, userId: subject });
    return { kind: 'adopted' };
  }
  useAuthStore.getState().setTokens({ accessToken: tokens.accessToken });
  broadcastAccessToken(tokens.accessToken);
  return { kind: 'refreshed' };
}

function tokenChangedSince(before: string | null): boolean {
  const now = getAccessToken();
  return !!now && now !== before;
}

/** Exponential backoff (1s, 2s, 4s… + jitter), or the server's Retry-After; capped. */
function retryDelayMs(attempt: number, retryAfterMs: number | null): number {
  const base = retryAfterMs ?? 1000 * 2 ** (attempt - 1) + Math.floor(Math.random() * 250);
  return Math.min(Math.max(base, 250), MAX_RETRY_WAIT_MS);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Run `fn` under the cross-tab refresh lock when the Web Locks API exists
 * (Safari/iOS 15.4+); otherwise just run it (per-tab single-flight still holds).
 * The lock covers ONE network attempt — backoff sleeps happen outside it — and
 * the wait for it is bounded, so a frozen background tab (iOS) holding it can't
 * hold every other tab hostage: on timeout we refresh uncoordinated (the
 * server's rotation grace window absorbs that race).
 */
async function withRefreshLock(fn: () => Promise<AttemptResult>): Promise<AttemptResult> {
  const locks: LockManager | undefined = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks || typeof locks.request !== 'function') return fn();
  try {
    const signal = typeof AbortSignal.timeout === 'function' ? AbortSignal.timeout(LOCK_WAIT_MS) : undefined;
    return await (signal ? locks.request(REFRESH_LOCK, { signal }, fn) : locks.request(REFRESH_LOCK, fn));
  } catch {
    // Lock wait timed out, or the lock manager is unusable (sandboxed frame).
    return fn();
  }
}

// Cross-tab token sharing.  Same-origin only (BroadcastChannel), and only ever
// the short-lived ACCESS token — the refresh token stays in its httpOnly cookie.
const authChannel = openAuthChannel();

function openAuthChannel(): BroadcastChannel | null {
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') return null;
  try {
    const channel = new BroadcastChannel(AUTH_CHANNEL);
    channel.onmessage = (event: MessageEvent) => adoptBroadcastToken(event.data);
    // Node's implementation (tests) would otherwise keep the process alive.
    (channel as unknown as { unref?: () => void }).unref?.();
    return channel;
  } catch {
    return null;
  }
}

function broadcastAccessToken(accessToken: string): void {
  try {
    authChannel?.postMessage({ type: 'access-token', accessToken });
  } catch {
    // Best effort — the other tabs will simply refresh for themselves.
  }
}

/** Exported for tests.  Adopt a token another tab just minted. */
export function adoptBroadcastToken(data: unknown): void {
  if (!data || typeof data !== 'object') return;
  const msg = data as { type?: unknown; accessToken?: unknown };
  if (msg.type !== 'access-token' || typeof msg.accessToken !== 'string' || !msg.accessToken) return;
  const state = useAuthStore.getState();
  // Never sign a logged-out tab in from the side channel — only keep an
  // existing session (or one this tab is restoring right now) fresh.
  if (!state.accessToken && (!pendingRefresh || pendingRefreshGeneration !== state.generation || pendingRefreshIdentity !== getAuthIdentityGeneration())) return;
  // The cookie jar is shared, so this is the same account — unless another
  // tab signed in as someone else; don't swap identities under this tab.
  const sub = jwtSubject(msg.accessToken);
  if (state.user && sub && sub !== state.user.id) return;
  state.setTokens({ accessToken: msg.accessToken });
}

function jwtSubject(token: string): string | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const sub = (JSON.parse(json) as { sub?: unknown }).sub;
    return typeof sub === 'string' ? sub : null;
  } catch {
    return null;
  }
}

/** Seconds from a readable `Retry-After` header (needs CORS expose) or the
 *  envelope's `error.retryAfterSec` (the auth endpoints always send it). */
function retryAfterSecFrom(response: Response, body: Partial<ApiErrorBody> | null): number | null {
  const header = Number(response.headers?.get?.('Retry-After') ?? NaN);
  if (Number.isFinite(header) && header > 0) return Math.ceil(header);
  const fromBody = (body?.error as { retryAfterSec?: unknown } | undefined)?.retryAfterSec;
  if (typeof fromBody === 'number' && Number.isFinite(fromBody) && fromBody > 0) return Math.ceil(fromBody);
  return null;
}

/** Never show "ThrottlerException: Too Many Requests" to a person. */
function withFriendlyRateLimitMessage(body: ApiErrorBody, retryAfterSec: number | null): ApiErrorBody {
  const raw = body.error.message ?? '';
  const generic = !raw || body.error.code === 'unexpected_error' || /throttlerexception|too many requests/i.test(raw);
  if (!generic) return body;
  const message =
    retryAfterSec !== null
      ? tr(apiMessages, 'rateLimitedWait', { seconds: String(retryAfterSec) })
      : tr(apiMessages, 'rateLimited');
  return { error: { ...body.error, code: body.error.code || 'rate_limited', message } };
}

async function safeJson<T>(response: Response, fallback: T): Promise<T> {
  try {
    return (await response.json()) as T;
  } catch {
    return fallback;
  }
}

/** Convenience: ULID-ish ID for idempotency keys. */
export function idempotencyKey(): string {
  // Browser-friendly random id; sortable property of ULID isn't required for cache keys.
  return (
    Date.now().toString(36) +
    Math.random().toString(36).slice(2, 10) +
    Math.random().toString(36).slice(2, 10)
  );
}
